import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  MapPin,
  Phone,
  Mail,
  Check,
  Instagram,
  Facebook,
  Loader2,
  ArrowRight,
  ChefHat,
  Wallet,
  TrendingUp,
  GraduationCap,
  Megaphone,
  Sparkles,
  Palette,
  Utensils,
  Smartphone,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import logoImg from '../assets/logo.png';
import heroDeliveryImg from '../assets/hero-delivery.png';
import founderNarendraImg from '../assets/founder-narendra.png';
import founderNavyaImg from '../assets/founder-navya.png';
import pizzaPaneerTikka from '../assets/pizza-paneer-tikka.png';
import pizzaTandoori from '../assets/pizza-tandoori.png';
import pizzaCheeseBurst from '../assets/pizza-cheese-burst.png';
import pizzaWhiteSauce from '../assets/pizza-white-sauce.png';
import pizzaPaneerCapsicum from '../assets/pizza-paneer-capsicum.png';
import outlet1 from '../assets/outlet-1.png';
import outlet2 from '../assets/outlet-2.png';
import outlet3 from '../assets/outlet-3.png';
import outlet4 from '../assets/outlet-4.png';
import outlet5 from '../assets/outlet-5.png';

const SIGNATURE_PIZZAS = [
  { img: pizzaPaneerTikka, name: 'Paneer Tikka Pizza', tag: 'Bestseller' },
  { img: pizzaTandoori, name: 'Tandoori Paneer Pizza', tag: 'Spicy' },
  { img: pizzaCheeseBurst, name: 'Cheese Burst Veggie', tag: 'Loaded' },
  { img: pizzaWhiteSauce, name: 'Creamy White Sauce', tag: 'New' },
  { img: pizzaPaneerCapsicum, name: 'Paneer Capsicum Pizza', tag: 'Classic' },
];

const OUTLET_IMAGES = [outlet1, outlet2, outlet3, outlet4, outlet5];

const ZOHO_SUBMIT_URL =
  'https://forms.zohopublic.in/pizzapurindiagm1/form/EventRegistration/formperma/eqkj4zpXkYL7uYk0_OSHxgkL9lRgY6x7W4w9AuAvJKA/htmlRecords/submit';

const TRUST_STATS = [
  { num: '15+', label: 'Active Outlets' },
  { num: '19+', label: 'Menu Items' },
  { num: '5yr', label: 'Secure Agreement' },
  { num: '₹0', label: 'Monthly Royalty' },
  { num: '8–10', label: 'Month ROI' },
];

const USPS = [
  {
    icon: Wallet,
    title: 'No Royalty & No Profit Sharing',
    desc: 'Jitna kamaao sab tumhara — make only profits. Koi monthly royalty nahi, kabhi bhi.',
    badge: 'UNIQUE',
    featured: true,
  },
  {
    icon: Sparkles,
    title: 'Premium Quality Sauces, Syrups & Premixes',
    desc: 'You get material directly from the manufacturer — best quality at the best price.',
  },
  {
    icon: ChefHat,
    title: 'Chef-Less Kitchen Model',
    desc: 'Chefs, professionals ya highly trained staff ki zaroorat nahi. SOPs itne simple hain — koi bhi chala sakta hai.',
    badge: 'KEY USP',
  },
  {
    icon: TrendingUp,
    title: 'Low Investment, High Return',
    desc: 'Most easy & affordable setup. 0% wastage — less inventory, more dishes with unique taste.',
  },
  {
    icon: Palette,
    title: '100% Creative Freedom',
    desc: 'Zero restrictions. Design your cafe as per your budget aur apne choice ke interior + furniture ke saath.',
  },
  {
    icon: Utensils,
    title: 'Affordable Menu & Customization',
    desc: '50+ variety of pure vegetarian food. Well-researched menu & recipes — plus freedom for menu customization.',
  },
  {
    icon: Megaphone,
    title: 'Influencer & Digital Marketing',
    desc: 'We allocate ₹25,000 marketing budget for your outlet — reach lakhs of people in your city.',
  },
  {
    icon: Smartphone,
    title: 'Technology-Driven End-to-End Support',
    desc: 'Digital ordering system, management, inventory etc — with full brand guidance and ongoing training.',
  },
];

const INVESTMENT_ROWS = [
  { label: 'Franchise Fee (one-time)', value: '₹1,50,000' },
  { label: 'Equipment & Setup', value: '₹2,00,000' },
  { label: 'Initial Raw Material', value: 'Included' },
  { label: 'Branding & Packaging Kit', value: 'Included' },
  { label: 'Training (Dual Centre)', value: 'Included' },
  { label: 'Monthly Royalty', value: '₹0', highlight: true },
  { label: 'Total Investment', value: '₹3.5 Lakh + GST', total: true },
];

const HIGHLIGHTS = [
  { big: '₹0', title: 'Monthly Royalty', sub: 'Kabhi bhi, kuch bhi nahi. Pure profit stays with you forever.' },
  { big: '8mo', title: 'Average ROI', sub: 'Recover karo investment 8-10 mahine mein hi.' },
  { big: '₹2L', title: 'Equipment Value', sub: 'Premium equipment included in franchise fee.' },
];

const CHECKLIST = [
  { title: 'Pizzapur Brand Name + Logo Rights', sub: null },
  { title: 'Complete Equipment Setup', sub: 'Oven, prep tables, refrigeration — sab ready' },
  { title: 'Raw Material & Supply Chain Support', sub: null },
  { title: 'Branded Packaging', sub: 'Boxes, bags, stickers — all Pizzapur branded' },
  { title: 'Zomato & Swiggy Onboarding Support', sub: null },
  { title: 'Social Media Creatives & Templates', sub: null },
  { title: 'Dual Training', sub: 'At our centre + at your location' },
  { title: 'Chefless Operations Training', sub: null },
  { title: 'Ongoing Support', sub: 'Call, WhatsApp & site visit' },
];

const COMPARISON = [
  { label: 'Total Investment', pizzapur: 'Rs. 3,50,000', other: 'Rs. 50 Lakh – 1 Crore+' },
  { label: 'Equipment', pizzapur: 'All equipment provided', other: 'Buy separately (5-10 Lakh+)' },
  { label: 'Monthly Royalty', pizzapur: 'Zero', other: '5–8% of monthly sales' },
  { label: 'Territory Exclusivity', pizzapur: '5 km protected zone', other: 'Limited or no protection' },
  { label: 'Training', pizzapur: 'Full training included', other: 'Paid separately' },
  { label: 'Marketing Support', pizzapur: 'Lifetime', other: 'Limited' },
  { label: 'Billing Software', pizzapur: '1 year free', other: 'Extra cost' },
  { label: 'Staff Uniform', pizzapur: 'Included', other: 'Extra cost' },
  { label: 'All-Veg Menu', pizzapur: '100% Vegetarian', other: 'Mixed' },
];

const STEPS = [
  { num: '1', icon: Phone, title: 'Apply Karo', desc: 'Form bharo ya WhatsApp karo. Hamari team 24 ghante mein call karegi.' },
  { num: '2', icon: Check, title: 'Agreement', desc: '5 saal ka secure franchise agreement sign karo. Territory lock ho jaati hai.' },
  { num: '3', icon: GraduationCap, title: 'Training', desc: 'Hamare centre pe + aapki location pe chefless operations training.' },
  { num: '4', icon: Megaphone, title: 'Launch', desc: 'Outlet launch karo, Zomato/Swiggy live karo, kamaana shuru karo.' },
];

const Home = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const formSectionRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('zf_referrer_name', '');
      payload.append('zf_redirect_url', '');
      payload.append('zc_gad', '');
      payload.append('Name_First', formData.firstName.trim());
      payload.append('Name_Last', formData.lastName.trim());
      payload.append('Email', formData.email.trim());
      payload.append('PhoneNumber_countrycode', formData.phone.trim());

      await fetch(ZOHO_SUBMIT_URL, {
        method: 'POST',
        mode: 'no-cors',
        body: payload,
      });

      toast.success(
        'Application received! Hamari team aapko 24 ghante ke andar call karegi.'
      );
      setFormData({ firstName: '', lastName: '', email: '', phone: '' });
    } catch (err) {
      console.error('Form submission error:', err);
      toast.error('Kuch galat ho gaya. Please retry ya call: +91 7877052363');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    if (formSectionRef.current) {
      formSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#142d16]/95 backdrop-blur-sm z-50 border-b border-[#FFB800]/20">
        <div className="container mx-auto px-4 md:px-8 py-3 flex justify-between items-center">
          <img src={logoImg} alt="Pizzapur" className="h-12 md:h-14" />
          <Button
            onClick={scrollToForm}
            data-testid="header-apply-now-btn"
            className="bg-[#C41E3A] hover:bg-[#A01828] text-white font-bold px-5 md:px-7 py-2 md:py-3 text-sm md:text-base rounded-lg transition-all duration-200"
          >
            APPLY NOW
          </Button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative bg-[#142d16] pt-28 md:pt-32 pb-16 md:pb-24 px-4 overflow-hidden">
        {/* radial accent (gold tint) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 80% 50%, rgba(255,184,0,0.18) 0%, transparent 65%)',
          }}
        />
        <div className="absolute right-[-80px] top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-[#FFB800] opacity-[0.05] pointer-events-none" />

        <div className="relative container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            {/* Left: text content */}
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold tracking-wide mb-5 md:mb-6">
                <span>🍕</span> India's Fastest Growing Pizza Chain
              </div>

              <h1
                className="font-bebas text-white leading-[0.92] tracking-wider mb-5"
                style={{ fontSize: 'clamp(44px, 7.5vw, 92px)' }}
              >
                Start Your
                <br />
                Pizza <span className="text-[#FFB800] block">Empire</span>
              </h1>

              <p className="text-white/70 text-sm md:text-base lg:text-lg max-w-xl leading-relaxed mb-7">
                India ka sabse easy-to-run pizza franchise. No chef. No gas. No royalty.
                Sirf pure profit aur exclusive territory.
              </p>

              <div className="inline-flex items-baseline gap-2 bg-[#FFB800] px-5 md:px-7 py-3 md:py-4 rounded-lg mb-7 shadow-xl shadow-[#FFB800]/20">
                <span className="text-[#142d16]/80 text-xs md:text-sm font-semibold">
                  Total Investment
                </span>
                <span className="font-bebas text-[#142d16] text-2xl md:text-4xl tracking-wide">
                  ₹3.5 Lakh
                </span>
                <span className="text-[#142d16]/70 text-xs md:text-sm font-semibold">
                  + GST
                </span>
              </div>

              <div className="flex flex-wrap gap-2 max-w-xl mb-8">
                <span className="inline-flex items-center gap-1.5 bg-white/[0.08] border border-white/15 text-white px-3.5 py-1.5 rounded-full text-xs md:text-sm font-medium">
                  ⚡ No Gas Cooking
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/[0.08] border border-white/15 text-white px-3.5 py-1.5 rounded-full text-xs md:text-sm font-medium">
                  👨‍🍳 No Chef Required
                </span>
                <span className="inline-flex items-center gap-1.5 bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] px-3.5 py-1.5 rounded-full text-xs md:text-sm font-semibold">
                  💰 Zero Royalty
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/[0.08] border border-white/15 text-white px-3.5 py-1.5 rounded-full text-xs md:text-sm font-medium">
                  📍 5KM Exclusive Territory
                </span>
              </div>

              <button
                onClick={scrollToForm}
                data-testid="hero-apply-now-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C41E3A] hover:bg-[#A01828] hover:-translate-y-0.5 text-white font-bold px-7 md:px-9 py-4 md:py-5 rounded-lg text-base md:text-lg transition-all duration-200 shadow-lg shadow-[#C41E3A]/30"
              >
                Apply for Franchise <ArrowRight className="h-5 w-5" />
              </button>
            </div>

            {/* Right: delivery boy image */}
            <div className="lg:col-span-6 order-1 lg:order-2 flex justify-center lg:justify-end">
              <img
                src={heroDeliveryImg}
                alt="Pizzapur Delivery"
                className="w-full max-w-[340px] sm:max-w-[420px] md:max-w-[500px] lg:max-w-full xl:max-w-[600px] h-auto relative z-10 drop-shadow-2xl"
                data-testid="hero-delivery-boy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR — Dark green + gold */}
      <div className="bg-[#142d16] border-y-2 border-[#FFB800]/30 py-5 md:py-6 px-4" data-testid="trust-bar">
        <div className="container mx-auto max-w-6xl flex flex-wrap justify-center items-center divide-x divide-[#FFB800]/30">
          {TRUST_STATS.map((s, i) => (
            <div key={i} className="flex items-center gap-3 px-5 md:px-8 py-2">
              <span className="font-bebas text-[#FFB800] text-3xl md:text-4xl leading-none">
                {s.num}
              </span>
              <span className="text-white/90 text-xs md:text-sm leading-tight max-w-[90px] font-medium">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* USPs — Teen Problems. Zero Tension. */}
      <section className="py-20 md:py-28 bg-[#2D6B2F] px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] text-xs font-bold tracking-widest uppercase mb-4">
              Why Pizzapur
            </span>
            <h2 className="font-bebas text-white text-5xl md:text-7xl leading-[0.95] tracking-wide mb-4">
              Teen Problems.
              <br />
              <span className="text-[#FFB800]">Zero Tension.</span>
            </h2>
            <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto">
              Jo cheezein food business ko mushkil banati hain — chef, gas, royalty —
              Pizzapur mein ek bhi nahi.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {USPS.map((u, i) => {
              const Icon = u.icon;
              return (
                <div
                  key={i}
                  className={`relative p-7 md:p-8 rounded-2xl border transition-all duration-300 hover:-translate-y-1 ${
                    u.featured
                      ? 'bg-[#142d16] border-[#FFB800]/50 lg:col-span-2'
                      : 'bg-[#1f4a21] border-white/10 hover:border-[#FFB800]/40'
                  }`}
                  data-testid={`usp-card-${i}`}
                >
                  {u.badge && (
                    <span className="absolute top-5 right-5 bg-[#FFB800] text-[#142d16] text-[10px] font-black px-2.5 py-1 rounded uppercase tracking-wider">
                      {u.badge}
                    </span>
                  )}
                  <Icon className="h-9 w-9 text-[#FFB800] mb-5" strokeWidth={1.8} />
                  <h3 className="font-bebas text-white text-2xl md:text-3xl mb-3 tracking-wide">
                    {u.title}
                  </h3>
                  <p className="text-white/75 text-sm md:text-base leading-relaxed">
                    {u.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SIGNATURE PIZZAS - Marquee */}
      <section className="py-20 bg-white overflow-hidden" data-testid="signature-pizzas-section">
        <div className="container mx-auto px-4 mb-12 max-w-6xl">
          <div className="text-center">
            <span className="inline-block px-4 py-1 rounded-full bg-[#FFB800]/10 border border-[#FFB800]/40 text-[#FFB800] text-xs font-bold tracking-widest uppercase mb-4">
              Crafted To Perfection
            </span>
            <h2 className="font-bebas text-[#142d16] text-5xl md:text-6xl leading-tight tracking-wide mb-4">
              Our Signature Pizzas
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
              Hand-tossed dough, premium ingredients, authentic Indian flavours.
            </p>
          </div>
        </div>

        <div
          className="relative w-full"
          style={{
            maskImage:
              'linear-gradient(to right, transparent 0, black 80px, black calc(100% - 80px), transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0, black 80px, black calc(100% - 80px), transparent 100%)',
          }}
        >
          <div className="flex gap-6 pizzapur-marquee">
            {[...SIGNATURE_PIZZAS, ...SIGNATURE_PIZZAS].map((p, idx) => (
              <div
                key={`pizza-${idx}`}
                className="flex-shrink-0 w-[260px] md:w-[320px] rounded-2xl overflow-hidden bg-white shadow-xl border border-gray-200 group"
                data-testid={`pizza-card-${idx}`}
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <span className="absolute top-3 left-3 bg-[#142d16] text-[#FFB800] text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#FFB800]/40">
                    {p.tag}
                  </span>
                </div>
                <div className="p-4 text-center">
                  <h3 className="font-bebas text-[#142d16] text-2xl tracking-wide">
                    {p.name}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTMENT DETAILS */}
      <section className="py-20 md:py-28 bg-[#2D6B2F] px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] text-xs font-bold tracking-widest uppercase mb-4">
              Investment Details
            </span>
            <h2 className="font-bebas text-white text-5xl md:text-7xl leading-[0.95] tracking-wide">
              Numbers Jo<br />
              <span className="text-[#FFB800]">Matter Karte Hain</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* table */}
            <div className="lg:col-span-3 bg-white rounded-2xl p-3 shadow-2xl" data-testid="investment-table">
              {INVESTMENT_ROWS.map((row, i) => (
                <div
                  key={i}
                  className={`flex justify-between items-center px-5 py-4 border-b border-gray-100 last:border-b-0 ${
                    row.total ? 'bg-[#142d16] text-white rounded-xl mt-1' : ''
                  }`}
                >
                  <span
                    className={`text-sm md:text-base ${
                      row.total
                        ? 'font-bold text-white'
                        : row.highlight
                        ? 'font-bold text-[#142d16]'
                        : 'text-gray-700'
                    }`}
                  >
                    {row.label}
                  </span>
                  <span
                    className={`font-bold text-base md:text-lg ${
                      row.total
                        ? 'font-bebas text-[#FFB800] text-2xl md:text-3xl tracking-wide'
                        : row.highlight
                        ? 'text-[#27AE60] text-xl'
                        : 'text-[#142d16]'
                    }`}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            {/* highlights */}
            <div className="lg:col-span-2 space-y-4">
              {HIGHLIGHTS.map((h, i) => (
                <div
                  key={i}
                  className="bg-[#142d16] border border-[#FFB800]/30 rounded-2xl p-6 hover:border-[#FFB800] transition-colors"
                  data-testid={`highlight-card-${i}`}
                >
                  <div className="font-bebas text-[#FFB800] text-5xl md:text-6xl leading-none mb-2">
                    {h.big}
                  </div>
                  <div className="text-white font-bold text-base mb-1">{h.title}</div>
                  <div className="text-white/65 text-sm leading-relaxed">{h.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CHECKLIST — Aapko Milega */}
      <section className="py-20 md:py-28 bg-white px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1 rounded-full bg-[#27AE60]/10 border border-[#27AE60]/30 text-[#27AE60] text-xs font-bold tracking-widest uppercase mb-4">
              What's Included
            </span>
            <h2 className="font-bebas text-[#142d16] text-5xl md:text-7xl leading-[0.95] tracking-wide">
              Aapko Milega —<br />
              <span className="text-[#2D6B2F]">Sab Kuch</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {CHECKLIST.map((c, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-[#FFB800]/5 hover:border-[#FFB800]/50 transition-all"
                data-testid={`checklist-item-${i}`}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#27AE60] flex items-center justify-center">
                  <Check className="h-5 w-5 text-white" strokeWidth={3} />
                </div>
                <div>
                  <div className="font-bold text-[#142d16] text-sm md:text-base">{c.title}</div>
                  {c.sub && <div className="text-gray-500 text-xs md:text-sm mt-1">{c.sub}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON — Why Choose Pizzapur */}
      <section className="py-20 md:py-28 bg-[#142d16] px-4 relative overflow-hidden" data-testid="comparison-section">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 55% 50% at 50% 50%, rgba(255,184,0,0.10) 0%, transparent 70%)',
          }}
        />
        <div className="relative container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] text-xs font-bold tracking-widest uppercase mb-4">
              Side-by-Side Comparison
            </span>
            <h2 className="font-bebas text-white text-5xl md:text-7xl leading-[0.95] tracking-wide mb-4">
              Why Choose<br />
              <span className="text-[#FFB800]">Pizzapur Franchise?</span>
            </h2>
            <p className="text-white/75 text-base md:text-lg max-w-2xl mx-auto">
              Dekho khud — Pizzapur vs Other Pizza Brands. Aap soch ke decide karo.
            </p>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-12 bg-[#142d16] text-white">
              <div className="col-span-4 px-6 py-5 text-sm font-bold uppercase tracking-wider">
                Feature
              </div>
              <div className="col-span-4 px-6 py-5 text-center text-sm font-black uppercase tracking-wider bg-[#FFB800] text-[#142d16] flex items-center justify-center gap-2">
                <img src={logoImg} alt="Pizzapur" className="h-7" />
              </div>
              <div className="col-span-4 px-6 py-5 text-center text-sm font-bold uppercase tracking-wider text-white/70">
                Other Pizza Brands
              </div>
            </div>

            {COMPARISON.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-12 border-b border-gray-100 last:border-b-0 ${
                  i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
                data-testid={`comparison-row-${i}`}
              >
                <div className="col-span-4 px-6 py-4 text-[#142d16] font-semibold text-sm md:text-base">
                  {row.label}
                </div>
                <div className="col-span-4 px-6 py-4 flex items-center justify-center gap-2 bg-[#27AE60]/5 border-l border-r border-[#27AE60]/15">
                  <Check className="h-5 w-5 text-[#27AE60] flex-shrink-0" strokeWidth={3} />
                  <span className="text-[#142d16] font-semibold text-sm md:text-base text-center">
                    {row.pizzapur}
                  </span>
                </div>
                <div className="col-span-4 px-6 py-4 flex items-center justify-center gap-2">
                  <X className="h-5 w-5 text-[#C41E3A] flex-shrink-0" strokeWidth={3} />
                  <span className="text-gray-500 text-sm md:text-base text-center">
                    {row.other}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile stacked cards */}
          <div className="md:hidden space-y-4">
            {COMPARISON.map((row, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-xl overflow-hidden"
                data-testid={`comparison-card-${i}`}
              >
                <div className="bg-[#142d16] text-white px-5 py-3 text-sm font-bold uppercase tracking-wider">
                  {row.label}
                </div>
                <div className="grid grid-cols-2 divide-x divide-gray-200">
                  <div className="p-4 flex flex-col items-center text-center gap-2 bg-[#27AE60]/5">
                    <div className="flex items-center gap-1">
                      <Check className="h-4 w-4 text-[#27AE60]" strokeWidth={3} />
                      <span className="text-[10px] uppercase tracking-wider font-bold text-[#27AE60]">
                        Pizzapur
                      </span>
                    </div>
                    <span className="text-[#142d16] font-semibold text-xs">{row.pizzapur}</span>
                  </div>
                  <div className="p-4 flex flex-col items-center text-center gap-2">
                    <div className="flex items-center gap-1">
                      <X className="h-4 w-4 text-[#C41E3A]" strokeWidth={3} />
                      <span className="text-[10px] uppercase tracking-wider font-bold text-[#C41E3A]">
                        Others
                      </span>
                    </div>
                    <span className="text-gray-500 text-xs">{row.other}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR LIVE OUTLETS — Marquee */}
      <section className="py-20 bg-[#142d16] overflow-hidden" data-testid="outlets-section">
        <div className="container mx-auto px-4 mb-12 max-w-6xl">
          <div className="text-center">
            <span className="inline-block px-4 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] text-xs font-bold tracking-widest uppercase mb-4">
              Trusted Across India
            </span>
            <h2 className="font-bebas text-white text-5xl md:text-6xl leading-tight tracking-wide mb-4">
              Our Live Outlets
            </h2>
            <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
              Street counters se modern dine-ins tak — Pizzapur family already thriving.
            </p>
          </div>
        </div>

        <div
          className="relative w-full"
          style={{
            maskImage:
              'linear-gradient(to right, transparent 0, black 80px, black calc(100% - 80px), transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0, black 80px, black calc(100% - 80px), transparent 100%)',
          }}
        >
          <div className="flex gap-6 pizzapur-marquee-reverse">
            {[...OUTLET_IMAGES, ...OUTLET_IMAGES].map((img, idx) => (
              <div
                key={`outlet-${idx}`}
                className="flex-shrink-0 w-[280px] md:w-[380px] aspect-square rounded-2xl overflow-hidden shadow-2xl border border-[#FFB800]/30 hover:border-[#FFB800] transition-all duration-500 group"
                data-testid={`outlet-card-${idx}`}
              >
                <img
                  src={img}
                  alt="Pizzapur Outlet"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIRF 4 STEPS */}
      <section className="py-20 md:py-28 bg-[#2D6B2F] px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] text-xs font-bold tracking-widest uppercase mb-4">
              How It Works
            </span>
            <h2 className="font-bebas text-white text-5xl md:text-7xl leading-[0.95] tracking-wide mb-4">
              Sirf <span className="text-[#FFB800]">4 Steps</span>
            </h2>
            <p className="text-white/80 text-base md:text-lg">
              Apply karo aaj — 30 din mein outlet ready.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className="relative bg-[#142d16] border border-[#FFB800]/20 rounded-2xl p-7 hover:border-[#FFB800] hover:-translate-y-1 transition-all duration-300"
                  data-testid={`step-card-${i}`}
                >
                  <div className="font-bebas text-[#FFB800]/30 text-6xl md:text-7xl leading-none absolute top-4 right-4">
                    {s.num}
                  </div>
                  <Icon className="h-9 w-9 text-[#FFB800] mb-5" strokeWidth={1.8} />
                  <h3 className="font-bebas text-white text-2xl tracking-wide mb-2">
                    {s.title}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEET OUR FOUNDERS */}
      <section className="py-20 md:py-28 bg-white px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#142d16] text-xs font-bold tracking-widest uppercase mb-4">
              Leadership
            </span>
            <h2 className="font-bebas text-[#142d16] text-5xl md:text-7xl leading-[0.95] tracking-wide mb-4">
              Meet Our<br />
              <span className="text-[#2D6B2F]">Visionaries</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
              The passionate leaders driving Pizzapur's success across India.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                img: founderNarendraImg,
                name: 'Narendra Chundawat',
                role: 'Founder & MD',
                quote:
                  'The visionary behind Pizzapur — building India\'s most affordable, royalty-free pizza franchise model, one outlet at a time.',
              },
              {
                img: founderNavyaImg,
                name: 'Navya Sankhala',
                role: 'Founder & Director',
                quote:
                  'The creative heart of Navyaji Food Industry — ensuring every Pizzapur outlet maintains the highest standards of taste and hospitality.',
              },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-[#2D6B2F] rounded-2xl overflow-hidden border-4 border-[#FFB800] shadow-2xl"
                data-testid={`founder-card-${i}`}
              >
                <div className="aspect-square bg-gradient-to-br from-[#e8efe5] to-[#d4dfd1] overflow-hidden">
                  <img
                    src={f.img}
                    alt={f.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 md:p-7 text-center">
                  <h3 className="font-bebas text-[#FFB800] text-3xl tracking-wide">{f.name}</h3>
                  <p className="text-[#FFB800] font-bold text-sm uppercase tracking-wider mb-3">
                    {f.role}
                  </p>
                  <p className="text-white/85 text-sm leading-relaxed">{f.quote}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLY FORM */}
      <section
        ref={formSectionRef}
        id="franchise-form"
        className="py-20 md:py-28 bg-[#142d16] px-4 relative overflow-hidden"
        data-testid="franchise-form-section"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 50% 60% at 30% 50%, rgba(255,184,0,0.12) 0%, transparent 65%)',
          }}
        />

        <div className="relative container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* left */}
            <div>
              <span className="inline-block px-4 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] text-xs font-bold tracking-widest uppercase mb-4">
                Apply Now
              </span>
              <h2 className="font-bebas text-white text-5xl md:text-7xl leading-[0.95] tracking-wide mb-6">
                Apni City Ka<br />
                <span className="text-[#FFB800]">Area Block Karo</span>
              </h2>
              <p className="text-white/75 text-base md:text-lg leading-relaxed mb-7">
                Limited franchises available. Jitna jaldi apply karo utna jaldi apna
                territory secure karo.
              </p>

              <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-xl p-5">
                <div className="text-[#FFB800] font-bold mb-2">
                  🔥 Apply karne ke baad kya milega?
                </div>
                <p className="text-white/80 text-sm leading-relaxed">
                  Hamare franchise team ka call 24 ghante ke andar aayega. Detailed
                  franchise kit, ROI calculator, aur city availability check — sab free
                  mein milega.
                </p>
              </div>
            </div>

            {/* form */}
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-7 md:p-9 shadow-2xl"
              data-testid="franchise-inquiry-form"
            >
              <h3 className="font-bebas text-[#142d16] text-3xl tracking-wide mb-1">
                Franchise Apply Karein
              </h3>
              <p className="text-gray-500 text-sm mb-6">Form bharo — 2 minute mein ho jaayega</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                    First Name <span className="text-[#FFB800]">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Aapka naam"
                    className="border-gray-300 focus-visible:ring-[#2D6B2F] focus-visible:border-[#2D6B2F] py-5 text-gray-800"
                    data-testid="input-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                    Last Name <span className="text-[#FFB800]">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last name"
                    className="border-gray-300 focus-visible:ring-[#2D6B2F] focus-visible:border-[#2D6B2F] py-5 text-gray-800"
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <Label htmlFor="phone" className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  WhatsApp Number <span className="text-[#FFB800]">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  inputMode="numeric"
                  pattern="[0-9+\s\-]{7,15}"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+91 98765 43210"
                  className="border-gray-300 focus-visible:ring-[#2D6B2F] focus-visible:border-[#2D6B2F] py-5 text-gray-800"
                  data-testid="input-phone"
                />
              </div>

              <div className="space-y-2 mb-6">
                <Label htmlFor="email" className="text-gray-700 text-xs font-bold uppercase tracking-wide">
                  Email Address <span className="text-[#FFB800]">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="aap@email.com"
                  className="border-gray-300 focus-visible:ring-[#2D6B2F] focus-visible:border-[#2D6B2F] py-5 text-gray-800"
                  data-testid="input-email"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                size="lg"
                data-testid="franchise-form-submit"
                className="w-full bg-[#C41E3A] hover:bg-[#A01828] text-white font-bold py-6 text-lg rounded-xl transition-all duration-300 disabled:opacity-70"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" /> Submitting...
                  </span>
                ) : (
                  <>
                    Apply for Franchise 🍕
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                ✓ Free consultation &nbsp;|&nbsp; ✓ No spam &nbsp;|&nbsp; ✓ Response within 24 hrs
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER CTA STRIP */}
      <section className="bg-[#142d16] py-16 md:py-20 px-4 text-center relative overflow-hidden" data-testid="footer-cta">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255,184,0,0.10) 0%, transparent 70%)',
          }}
        />
        <div className="relative container mx-auto max-w-3xl">
          <h2 className="font-bebas text-white text-5xl md:text-7xl leading-[0.95] tracking-wide mb-3">
            No Chef. No Gas.<br />
            <span className="text-[#FFB800]">No Royalty.</span>
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8">
            Sirf pure profit. India ka sabse easy food franchise model.
          </p>
          <button
            onClick={scrollToForm}
            data-testid="footer-cta-apply-btn"
            className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01828] hover:-translate-y-0.5 text-white font-bold px-8 py-4 rounded-lg text-base md:text-lg transition-all duration-200 shadow-xl shadow-[#C41E3A]/30"
          >
            Apply Now — Free <ArrowRight className="h-5 w-5" />
          </button>

          <div className="flex flex-wrap justify-center gap-4 md:gap-6 mt-8 text-white">
            <a
              href="https://wa.me/917877052363"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm hover:text-[#FFB800] transition-colors"
            >
              📱 +91 78770 52363
            </a>
            <a
              href="mailto:contact@pizzapur.com"
              className="inline-flex items-center gap-2 text-sm hover:text-[#FFB800] transition-colors"
            >
              📧 contact@pizzapur.com
            </a>
            <a
              href="https://www.pizzapur.com"
              className="inline-flex items-center gap-2 text-sm hover:text-[#FFB800] transition-colors"
            >
              🌐 www.pizzapur.com
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER (UNCHANGED — old footer) */}
      <footer className="bg-[#2D6B2F] text-white border-t-4 border-[#FFB800]" data-testid="site-footer">
        <div className="container mx-auto px-4 pt-16 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Column 1 - Logo + Brand */}
            <div className="space-y-5">
              <img
                src={logoImg}
                alt="Pizzapur"
                style={{ width: '160px', height: 'auto' }}
                data-testid="footer-logo"
              />
              <p className="text-sm text-white/70 leading-relaxed max-w-xs">
                India's Most Affordable &amp; Profitable Pizza Franchise
              </p>
              <div className="w-12 h-px bg-white/40" />
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-white/50">A product of</p>
                <p className="text-sm font-semibold text-white">
                  Navyaji Food Industry Private Limited
                </p>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <a
                  href="https://www.instagram.com/pizzapur/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="footer-social-instagram"
                  className="w-10 h-10 rounded-full border border-white/60 flex items-center justify-center text-white hover:bg-white hover:text-[#2D6B2F] transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href="https://www.facebook.com/pizzapurindia"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  data-testid="footer-social-facebook"
                  className="w-10 h-10 rounded-full border border-white/60 flex items-center justify-center text-white hover:bg-white hover:text-[#2D6B2F] transition-colors"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Column 2 - Contact */}
            <div className="space-y-5">
              <h3 className="text-base font-semibold text-white uppercase tracking-wider">
                Contact
              </h3>
              <div className="w-12 h-px bg-white/40" />
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Mail className="h-4 w-4 text-white/70 flex-shrink-0 mt-0.5" />
                  <a
                    href="mailto:contact@pizzapur.com"
                    className="text-white/80 hover:text-white transition-colors"
                    data-testid="footer-email"
                  >
                    contact@pizzapur.com
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="h-4 w-4 text-white/70 flex-shrink-0 mt-0.5" />
                  <a
                    href="https://api.whatsapp.com/send/?phone=917877052363"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition-colors"
                    data-testid="footer-whatsapp"
                  >
                    WhatsApp: 7877052363
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-white/70 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Kudi Sector 2, Jodhpur, Rajasthan, India</span>
                </li>
              </ul>
            </div>

            {/* Column 3 - Regulatory */}
            <div className="space-y-5">
              <h3 className="text-base font-semibold text-white uppercase tracking-wider">
                Regulatory Info
              </h3>
              <div className="w-12 h-px bg-white/40" />
              <ul className="space-y-4 text-sm">
                <li>
                  <p className="text-xs uppercase tracking-wider text-white/50 mb-1">
                    DPIIT Certificate No.
                  </p>
                  <p className="text-white/90 font-medium" data-testid="footer-dpiit">DIPP254838</p>
                </li>
                <li>
                  <p className="text-xs uppercase tracking-wider text-white/50 mb-1">CIN</p>
                  <p className="text-white/90 font-medium" data-testid="footer-cin">U56102RJ2024PTC097625</p>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/15 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/60 text-center md:text-left">
              © 2025 Navyaji Food Industry Private Limited. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-xs">
              <Link to="/legal/privacy" className="text-white/70 hover:text-white transition-colors" data-testid="footer-link-privacy">
                Privacy Policy
              </Link>
              <Link to="/legal/terms" className="text-white/70 hover:text-white transition-colors" data-testid="footer-link-terms">
                Terms &amp; Conditions
              </Link>
              <Link to="/legal/refund" className="text-white/70 hover:text-white transition-colors" data-testid="footer-link-refund">
                Refund Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
